/**
 * Document-Client Assignment Service
 * Service for managing the relationship between documents and clients
 * Uses the DocumentClient bridge table API
 */

import { BaseService } from './BaseService';

/**
 * Document-Client Assignment Service Class
 * Manages document-client relationships through the DocumentClient table
 */
class DocumentClientAssignmentService extends BaseService {
    constructor() {
        super('/api/document-clients');
    }

    /**
     * Assign a document to a client
     * Creates a relationship in the DocumentClient bridge table
     * @param {string} accessToken - JWT access token
     * @param {number} documentId - Document ID
     * @param {number} clientId - Client ID
     * @returns {Promise<Object>} Created assignment details with document and client info
     */
    async assignDocumentToClient(accessToken, documentId, clientId) {
        try {
            const response = await this.makeRequest(accessToken, '', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    documentId,
                    clientId
                })
            });
            
            return response;
        } catch (error) {
            console.error('❌ Failed to assign document to client:', error);
            throw error;
        }
    }

    /**
     * Get a specific document-client assignment
     * @param {string} accessToken - JWT access token
     * @param {number} documentId - Document ID
     * @param {number} clientId - Client ID
     * @returns {Promise<Object>} Assignment details
     */
    async getAssignment(accessToken, documentId, clientId) {
        try {
            return await this.makeRequest(accessToken, `/document/${documentId}/client/${clientId}`);
        } catch (error) {
            console.error(`Failed to fetch assignment for document ${documentId} and client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get all clients assigned to a specific document
     * @param {string} accessToken - JWT access token
     * @param {number} documentId - Document ID
     * @returns {Promise<Array>} Array of assignments with client details
     */
    async getDocumentAssignments(accessToken, documentId) {
        try {
            return await this.makeRequest(accessToken, `/document/${documentId}`);
        } catch (error) {
            console.error(`Failed to fetch assignments for document ${documentId}:`, error);
            throw error;
        }
    }

    /**
     * Get all documents assigned to a specific client
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @returns {Promise<Array>} Array of assignments with document details
     */
    async getClientAssignments(accessToken, clientId) {
        try {
            return await this.makeRequest(accessToken, `/client/${clientId}`);
        } catch (error) {
            console.error(`Failed to fetch assignments for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get assignments by container (all documents in a specific container)
     * @param {string} accessToken - JWT access token
     * @param {string} container - Container name
     * @returns {Promise<Array>} Array of assignments
     */
    async getAssignmentsByContainer(accessToken, container) {
        try {
            return await this.makeRequest(accessToken, `/container/${encodeURIComponent(container)}`);
        } catch (error) {
            console.error(`Failed to fetch assignments for container ${container}:`, error);
            throw error;
        }
    }

    /**
     * Get client assignments in a specific container
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {string} container - Container name
     * @returns {Promise<Array>} Array of assignments
     */
    async getClientAssignmentsByContainer(accessToken, clientId, container) {
        try {
            return await this.makeRequest(accessToken, `/client/${clientId}/container/${encodeURIComponent(container)}`);
        } catch (error) {
            console.error(`Failed to fetch assignments for client ${clientId} in container ${container}:`, error);
            throw error;
        }
    }

    /**
     * Search assignments by filename pattern
     * @param {string} accessToken - JWT access token
     * @param {string} pattern - Search pattern (case-insensitive)
     * @returns {Promise<Array>} Array of matching assignments
     */
    async searchByFilename(accessToken, pattern) {
        try {
            return await this.makeRequest(accessToken, `/search?filename=${encodeURIComponent(pattern)}`);
        } catch (error) {
            console.error(`Failed to search assignments by filename ${pattern}:`, error);
            throw error;
        }
    }

    /**
     * Bulk assign multiple clients to a document
     * @param {string} accessToken - JWT access token
     * @param {number} documentId - Document ID
     * @param {Array<number>} clientIds - Array of client IDs
     * @returns {Promise<Array>} Array of created assignments
     */
    async bulkAssignClientsToDocument(accessToken, documentId, clientIds) {
        try {
            const response = await this.makeRequest(accessToken, `/document/${documentId}/bulk-assign`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(clientIds)
            });
            return response;
        } catch (error) {
            console.error(`Failed to bulk assign clients to document ${documentId}:`, error);
            throw error;
        }
    }

    /**
     * Bulk assign multiple documents to a client
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @param {Array<number>} documentIds - Array of document IDs
     * @returns {Promise<Array>} Array of created assignments
     */
    async bulkAssignDocumentsToClient(accessToken, clientId, documentIds) {
        try {
            const response = await this.makeRequest(accessToken, `/client/${clientId}/bulk-assign`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(documentIds)
            });
            return response;
        } catch (error) {
            console.error(`Failed to bulk assign documents to client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Remove a specific document-client assignment
     * @param {string} accessToken - JWT access token
     * @param {number} documentId - Document ID
     * @param {number} clientId - Client ID
     * @returns {Promise<void>}
     */
    async removeAssignment(accessToken, documentId, clientId) {
        try {
            return await this.makeRequest(accessToken, `/document/${documentId}/client/${clientId}`, {
                method: 'DELETE'
            });
        } catch (error) {
            console.error(`Failed to remove assignment for document ${documentId} and client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Remove all client assignments for a document
     * @param {string} accessToken - JWT access token
     * @param {number} documentId - Document ID
     * @returns {Promise<void>}
     */
    async removeAllDocumentAssignments(accessToken, documentId) {
        try {
            return await this.makeRequest(accessToken, `/document/${documentId}`, {
                method: 'DELETE'
            });
        } catch (error) {
            console.error(`Failed to remove all assignments for document ${documentId}:`, error);
            throw error;
        }
    }

    /**
     * Remove all document assignments for a client
     * @param {string} accessToken - JWT access token
     * @param {number} clientId - Client ID
     * @returns {Promise<void>}
     */
    async removeAllClientAssignments(accessToken, clientId) {
        try {
            return await this.makeRequest(accessToken, `/client/${clientId}`, {
                method: 'DELETE'
            });
        } catch (error) {
            console.error(`Failed to remove all assignments for client ${clientId}:`, error);
            throw error;
        }
    }

    /**
     * Get assignment statistics
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Object>} Statistics object
     */
    async getStatistics(accessToken) {
        try {
            return await this.makeRequest(accessToken, '/statistics');
        } catch (error) {
            console.error('Failed to fetch assignment statistics:', error);
            throw error;
        }
    }

    /**
     * Upload document and create client assignment in one operation
     * This uploads the document and then creates the assignment using the DocumentClient API
     * @param {string} accessToken - JWT access token
     * @param {File} file - File to upload
     * @param {number} clientId - Client ID to assign to
     * @param {string} container - Container name
     * @param {string} [fileName] - Optional custom filename
     * @returns {Promise<Object>} Upload response with document details and assignment
     */
    async uploadAndAssignDocument(accessToken, file, clientId, container, fileName = null) {
        try {
            // Step 1: Upload the document
            const formData = new FormData();
            formData.append('file', file);
            formData.append('container', container);
            if (fileName) {
                formData.append('fileName', fileName);
            }

            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
            const uploadResponse = await fetch(`${API_BASE_URL}/api/v1/documents/upload`, {
                method: 'POST',
                mode: 'cors',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: formData,
            });

            if (!uploadResponse.ok) {
                let errorMessage = `Upload failed: ${uploadResponse.statusText}`;
                
                try {
                    // Try to parse as JSON first (most common format)
                    const errorData = await uploadResponse.json();
                    
                    // Extract the actual error message from various possible formats
                    if (errorData.message) {
                        errorMessage = errorData.message;
                    } else if (errorData.error) {
                        errorMessage = errorData.error;
                    } else if (errorData.errorMessage) {
                        errorMessage = errorData.errorMessage;
                    } else if (typeof errorData === 'string') {
                        errorMessage = errorData;
                    }
                    
                    // Clean up nested error messages if present
                    if (errorMessage.includes('Failed to upload document:')) {
                        errorMessage = errorMessage.split('Failed to upload document:').pop().trim();
                    }
                } catch (jsonError) {
                    // If not JSON, try as plain text
                    try {
                        const errorText = await uploadResponse.text();
                        if (errorText && errorText.trim().length > 0) {
                            errorMessage = errorText;
                        }
                    } catch (textError) {
                        // Keep the default error message
                    }
                }
                
                console.error('Upload failed:', {
                    status: uploadResponse.status,
                    statusText: uploadResponse.statusText,
                    errorMessage
                });
                
                throw new Error(errorMessage);
            }

            const uploadResult = await uploadResponse.json();

            // Step 2: Create the document-client assignment
            if (uploadResult.document && uploadResult.document.documentId) {
                const documentId = uploadResult.document.documentId;
                
                try {
                    const assignment = await this.assignDocumentToClient(accessToken, documentId, clientId);
                    
                    // Return combined result
                    return {
                        ...uploadResult,
                        assignment
                    };
                } catch (assignmentError) {
                    console.error('❌ Failed to create document-client assignment:', assignmentError);
                    // Document was uploaded but assignment failed
                    // Return the upload result but include the error
                    return {
                        ...uploadResult,
                        assignmentError: assignmentError.message
                    };
                }
            }

            return uploadResult;
        } catch (error) {
            console.error('Failed to upload and assign document:', error);
            throw error;
        }
    }
}

// Create singleton instance
const documentClientAssignmentService = new DocumentClientAssignmentService();

// Export both class and instance
export { DocumentClientAssignmentService, documentClientAssignmentService };

// Maintain backward compatibility with old import name
export { documentClientAssignmentService as clientDocumentLinkService };
export default documentClientAssignmentService;
